/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderUserContext,
  FormState,
  FormStateBuilder,
  PrinterBusinessService,
  PrinterFinderListItem,
  REPORT_VENDOR_SCHEDULE,
  ReportService,
  whenCreateMode,
} from '@gh/builder-shared';
import { BuilderPermission, ContactDeliveryMethod, ContactVendorListItemDto } from '@gh/builder-shared-data';
import { createDialogRoute, DateTimeTypes, getDialogInitData, toAppFormControl } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { RouteDataService } from '@gh/core-data';
import { LocalizationService, TranslationService } from '@gh/core-mls';
import {
  AutocompleteLoadMoreEvent,
  DialogInput,
  DialogService,
  FormMode,
  getControl,
  getControlValue,
  newFormControl,
  NotificationService,
  setControlValue,
  TextMessageDialogConfig,
  WaitingService,
} from '@gh/core-ui';
import { GridDefinition, GridDefinitionFactory } from '@gh/core-ui-grid';
import {
  addWeeks,
  createLink,
  currentNormalizedDate,
  endOfWeek,
  isDateValid,
  isNotNil,
  startOfWeek,
} from '@gh/core-util';

import { concat, eq, map, remove, some } from 'lodash';

import { ScheduleListBusinessService } from '../../resources/schedule-list-business.service';
import {
  ContactVendorListItem,
  isSalespersonSelectTypeAllVendors,
  SalespersonSelectType,
  ScheduleVendorFilterDialogConfig,
  ScheduleVendorReportRequest,
  vendorReportRequestToReportRequest,
} from '../../resources/schedule-list.model';
import { textMessageDialogRoute } from '@gh/common-internal-shared';
import { Observable } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { GoogleAuthService } from '../../../core-external';

@Component({
  selector: 'gh-schedule-vendor-filter-dialog',
  template: require('./schedule-vendor-filter-dialog.component.html'),
  styles: [require('./schedule-vendor-filter-dialog.component.scss')],
})
export class ScheduleVendorFilterDialogComponent implements OnInit, DialogInput<ScheduleVendorFilterDialogConfig> {
  input: ScheduleVendorFilterDialogConfig;
  form: FormState<ScheduleVendorReportRequest>;
  mailDeliveryExists: boolean;

  printerName = newFormControl('');
  printerList: PrinterFinderListItem[] = [];
  isCloud: boolean;
  originalPrinterList: PrinterFinderListItem[];
  printerGridDefinition: GridDefinition;

  constructor(@Inject(MAT_DIALOG_DATA) private dialogData: any,
              private dialogRef: MatDialogRef<any>,
              private route: ActivatedRoute,
              private googleAuth: GoogleAuthService,
              private routeData: RouteDataService,
              private userContext: BuilderUserContext,
              private waitingService: WaitingService,
              private printerBusinessService: PrinterBusinessService,
              private notificationService: NotificationService,
              private formStateBuilder: FormStateBuilder,
              private businessService: ScheduleListBusinessService,
              private reportService: ReportService,
              private translationService: TranslationService,
              private dialogs: DialogService,
              private gridDefinitionFactory: GridDefinitionFactory,
              private localizationService: LocalizationService) {
    this.isStartDateAllowed = this.isStartDateAllowed.bind(this);
    this.isEndDateAllowed = this.isEndDateAllowed.bind(this);
  }

  get hasPrinters(): boolean {
    return this.printerList.length > 0;
  }

  get vendorList(): ContactVendorListItem[] {
    return getControlValue(this.form.group, 'vendorList');
  }

  get isDeliverDisabled(): boolean {
    return !this.isVendorSelectedAndReceiving
      || (!this.isSendSuppliersSelected() && !this.form.group.get('sendSupervisors')?.value)
      || (this.isCloud && this.isPrinterSectionActive && !this.isPrinterSelected())
      || !this.isPreviewAllowed();
  }

  get isPrinterSectionVisible(): boolean {
    return this.isSendSuppliersSelected() && this.isPrinterSectionActive;
  }

  get isPrinterSectionActive(): boolean {
    const type = getControlValue(this.form.group, 'salespersonSelectType');
    if (type === SalespersonSelectType.All) {
      return this.mailDeliveryExists;
    } else {
      return some(
        getControlValue(this.form.group, 'vendorList'),
        ({ deliveryMethod }: ContactVendorListItem) => deliveryMethod === ContactDeliveryMethod.Mail);
    }
  }

  get isVendorSelectedAndReceiving(): boolean {
    const type = getControlValue(this.form.group, 'salespersonSelectType');
    const vendorList = getControlValue(this.form.group, 'vendorList');
    return type === SalespersonSelectType.All ||
      (vendorList.length > 0 && !!vendorList.find((vendor: ContactVendorListItemDto) => !vendor.excludeReceivingVendorReport));
  }

  isPrinterSelected(): boolean {
    return isNotNil(getControlValue(this.form.group, 'printerId'));
  }

  ngOnInit(): void {
    const { printerList, mailDeliveryExists } = getDialogInitData(this.dialogData);
    this.printerList = printerList.cloudPrinters;
    this.isCloud = printerList.isCloud;
    this.mailDeliveryExists = mailDeliveryExists;
    this.originalPrinterList = this.printerList;
    this.printerGridDefinition = this.buildPrinterGridDefinition();
    this.form = this.formStateBuilder.buildPlain({
      mode: FormMode.CreateMode,
      enabled: whenCreateMode(),
      value: {
        vendorList: [],
        from: startOfWeek(currentNormalizedDate()),
        to: endOfWeek(addWeeks(currentNormalizedDate(), 4)), // tslint:disable-line:no-magic-numbers
        worksheetFormat: false,
        showCompletedTasks: false,
        sendSuppliers: false,
        sendSupervisors: false,
        printerId: void 0,
        salespersonSelectType: SalespersonSelectType.All,
      },
      children: {
        salespersonSelectType: {
          required: true,
        },
        vendorList: {
          required: true,
          enabled: false,
        },
        from: {
          required: true,
          type: DateTimeTypes.ShortDate,
        },
        to: {
          required: true,
          type: DateTimeTypes.ShortDate,
        },
        worksheetFormat: void 0,
        sendSuppliers: void 0,
        sendSupervisors: void 0,
        showCompletedTasks: void 0,
        printerId: void 0,
      },
    });
  }

  onDeliver(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.isCloud) {
      this.printerBusinessService.grantGoogleOfflineAccess().pipe(
        this.waitingService.operator())
        .subscribe((isConnected: boolean) => {
          this.businessService.deliverScheduleVendorReport({ ...this.form.submitValue, authCode: this.googleAuth.accessToken }).pipe(
            this.waitingService.operator())
            .subscribe(
              () => this.closeDialogOnSuccess(),
              this.notificationService.processAll);
        }, this.notificationService.processAll); // tslint:disable-line:align
    } else {
      this.businessService.deliverScheduleVendorReport(this.form.submitValue).pipe(
        this.waitingService.operator())
        .subscribe(
          () => this.closeDialogOnSuccess(),
          this.notificationService.processAll);
    }
  }

  loadVendorContacts({ query, load }: AutocompleteLoadMoreEvent): void {
    load(this.businessService.findAllVendorsAsDataSource(query, this.getVendorListIds()));
  }

  onVendorSelectTypeChange({ value }: MatRadioChange): void {
    if (isSalespersonSelectTypeAllVendors(value)) {
      const vendorListControl = toAppFormControl(getControl(this.form.group, 'vendorList'));
      vendorListControl.updateSchema({
        enabled: false,
      });
      vendorListControl.setValue([]);
    } else {
      toAppFormControl(getControl(this.form.group, 'vendorList')).updateSchema({
        enabled: true,
      });
    }
  }

  onVendorSelect(vendor: ContactVendorListItem): void {
    if (vendor) {
      const vendorList = getControlValue(this.form.group, 'vendorList');
      setControlValue(this.form.group, 'vendorList', concat(vendorList, vendor));
    }
  }

  onRemoveVendor({ contactId }: ContactVendorListItem): void {
    const vendorList = getControlValue(this.form.group, 'vendorList');
    remove(vendorList, (vendor: ContactVendorListItem) => vendor.contactId === contactId);
    setControlValue(this.form.group, 'vendorList', vendorList);
  }

  onSendSuppliersSupervisorsChange({ checked }: MatCheckboxChange): void {
    this.form.get('worksheetFormat').updateSchema({
      enabled: !checked,
    });
    if (checked) {
      setControlValue(this.form.group, 'worksheetFormat', false);
    }
  }

  onSearchPrinter(): void {
    const localizationHelper = this.localizationService.getStringL10nHelper();
    const name = this.printerName.value;
    this.printerList = name
      ? this.originalPrinterList.filter(({ cloudPrinterName }) =>
        localizationHelper.includesIgnoreCase(cloudPrinterName, name))
      : this.originalPrinterList;
  }

  onSelectPrinter({ cloudPrinterId }: PrinterFinderListItem): void {
    setControlValue(this.form.group, 'printerId', cloudPrinterId);
  }

  isPrinterChecked({ cloudPrinterId }: PrinterFinderListItem): boolean {
    return eq(getControlValue(this.form.group, 'printerId'), cloudPrinterId);
  }

  isStartDateAllowed(date: Date): boolean {
    const endDate = getControlValue(this.form.group, 'to');
    return isNotNil(endDate) && isDateValid(endDate) ? date <= endDate : true;
  }

  isEndDateAllowed(date: Date): boolean {
    const startDate = getControlValue(this.form.group, 'from');
    return isNotNil(startDate) && isDateValid(startDate) ? date >= startDate : true;
  }

  isSendSuppliersSelected(): boolean {
    return getControlValue(this.form.group, 'sendSuppliers') === true;
  }

  isPreviewAllowed(): boolean {
    return this.userContext.hasPermission(BuilderPermission.Reports.Schedules.VENDOR_REPORT);
  }

  onPreview(): void {
    this.reportService.generateUrlAndShowDialog(
      REPORT_VENDOR_SCHEDULE,
      vendorReportRequestToReportRequest(this.form.submitValue),
    ).subscribe();
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private buildPrinterGridDefinition(): GridDefinition {
    return this.gridDefinitionFactory.buildManual([{
      field: 'cloudPrinterName',
      headerName: this.translationService.get('lbl_printer_name'),
    }],                                           {
      getRowNodeId: ({ cloudPrinterId }: PrinterFinderListItem): ID => cloudPrinterId,
    });
  }

  private closeDialogOnSuccess(): void {
    this.notificationService.success('msg_vendor_report_tasks_added');
    this.dialogRef.close({});
  }

  private getVendorListIds(): ID[] {
    return map(getControlValue(this.form.group, 'vendorList'), 'contactId');
  }

  private showReportDialog(report: any): Observable<void> {
    return this.dialogs.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
      title: this.translationService.get('Link to Print Vendor Reports'),
      html: [
        this.translationService.get('Please follow the link below to print Vendor Reports'),
        createLink(report.s3Link, 'Your file'),
      ].join(' '),
      buttons: [{ value: 'ok', title: this.translationService.get('lbl_ok'), type: 'primary' }],
    }).afterClosed();
  }
}

export const scheduleVendorReportFilterDialogRoute = createDialogRoute({
  component: ScheduleVendorFilterDialogComponent,
  init: {
    service: ScheduleListBusinessService,
    selector: 'initScheduleVendorFilterDialog',
  },
});
